import React, { PropsWithChildren, ReactNode, useEffect, useState } from "react";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import { FieldHookConfig, useField } from "formik";
import { InputLabel } from "@mui/material";

const ITEM_HEIGHT = 32;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: (ITEM_HEIGHT * 4.5) + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export type MultiSelectProps = {
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  children?: ReactNode;
  name: string;
  options: string[];
  label?: string;
  fullWidth?: boolean;
  margin?: "dense" | "normal" | "none";
  disabled?: boolean;
  required?: boolean;
}

type ItemProps = {
  option: string;
  value?: string[];
}

const renderItemOption = ({ option, value }: ItemProps, index: number): ReactNode => (
  <MenuItem value={option} key={`select-${value}-${index}`}>
    <Checkbox checked={value && value.indexOf(option) > -1} />
    <ListItemText primary={option} />
  </MenuItem>
);

export const MultiSelect: React.FC<PropsWithChildren<MultiSelectProps & FieldHookConfig<any>>> =
// eslint-disable-next-line max-lines-per-function
(props: MultiSelectProps & FieldHookConfig<any>) => {
  const [field, meta, helpers] = useField(props);
  const {
    name,
    children,
    options,
    fullWidth,
    margin,
    disabled,
    required,
    open,
    onOpen,
    onClose,
  } = props;
  const { value } = field;
  const { touched, error } = meta;
  const { setValue, setTouched } = helpers;

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    setValue(e.target.value);
    setTouched(true);
  };

  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const [usedOptions, setUsedOptions] = useState<string[]>([]);
  useEffect(() => options && setUsedOptions(options), [options]);
  const salt = Math.floor(Math.random() * 10000);
  const labelNode = <InputLabel id={`${name}-label-${salt}`} variant="outlined">{props.label}</InputLabel>;

  return (
    <FormControl
      variant="outlined"
      hiddenLabel={!props.label}
      margin={margin || "dense"}
      fullWidth={fullWidth}
    >
      {props.label && labelNode}
      <Select
        labelId={props.label && `${name}-label-${salt}`}
        displayEmpty
        multiple
        name={name}
        value={value}
        disabled={disabled}
        required={required}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onClick={() => setTouched(true)}
        onChange={handleChange}
        renderValue={(selected) => (
          Array.isArray(selected) ? (
            (selected.length > 3 && `Selected (${selected.length})`) || selected.join(", ")
          ) : selected) as ReactNode}
        MenuProps={MenuProps}
        SelectDisplayProps={{ style: {
          minWidth: "7em",
        } }}
        inputProps={{ size: margin === "dense" ? "small" : "medium" }}
      >
        {usedOptions.map((option) => ({ option, value })).map(renderItemOption)}
        {children}
      </Select>
      {touched && error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
